export enum Currency {
    RSD = 'RSD',
    EUR = 'EUR',
    RUB = 'RUB',
    CHF = 'CHF',
    USD = 'USD',
    BAM = 'BAM',
}

export interface User {
    firstName: string;
    lastName: string;
    name: string;
    email: string;
    role: UserRole;
    avatar?: Uint8Array;
    /** Keycloak `preferred_username` claim (loguje se kao username, ne email). Koristi se
     *  za Monri verification gate (uporedjuje sa REACT_APP_MONRI_USERNAME). */
    username?: string;
}

export enum UserRole {
    ADMIN = 'ADMIN',
    USER = 'USER',
    THERAPIST = 'THERAPIST',
}

export type LoginDTO = {
    username: string;
    password: string;
};

export const EmptyLoginDTO: LoginDTO = {
    username: '',
    password: '',
};

export type ResetPasswordDTO = {
    newPassword: string;
    token: string | null;
};

export type AuthResponse = {
    access_token: string;
};

export type Therapist = {
    id: number;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    passwordHash?: string;
    email: string;
    therapistLanguages: TherapistLanguage[];
    therapyPrices: Price[];
    avatar?: Uint8Array;
    therapistStatus?: string;
    username: string;
    role?: string | undefined;
    specializationEn?: string;
    solutions?: string;
    videoLink?: string;
    subscription?: boolean;
    premium?: boolean;
    supportPasssport?: boolean;
    problems?: string[];
};

export const EmptyTherapist: Therapist = {
    id: 0,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    therapistLanguages: [],
    therapyPrices: [],
    username: '',
    role: '',
    specializationEn: '',
    solutions: '',
    videoLink: '',
    subscription: false,
    premium: false,
    supportPasssport: false,
    problems: [],
};

export type Patient = {
    id?: number;
    email: string;
    username: string;
    password: string;
    firstName: string;
    lastName: string;
};

export const EmptyPatient: Patient = {
    id: 0,
    email: '',
    username: '',
    password: '',
    firstName: '',
    lastName: '',
};

export type Price = {
    currency: string;
    priceIndividual: number;
    priceGroup: number;
};

export type TherapistLanguage = {
    specialization?: string;
    description?: string;
    langId?: number;
    place?: string;
    schooling?: string;
    solutions?: string;
    note?: string;
};

export const EmptyTherapistLanguage: TherapistLanguage = {
    specialization: '',
    description: '',
    langId: 0,
    place: '',
    schooling: '',
    solutions: '',
    note: '',
};

export enum ModalAction {
    NONE,
    DELETE_THERAPIST,
    DEACTIVATE_THERAPIST,
    ACTIVATE_THERAPIST,
    INACTIVATE_THERAPIST,
    CANCEL_SESSION,
    FINISH_SESSION,
    LOGOUT,
    DELETE_PATIENT,
    DELETE_WORKSHOP,
}

export type Language = {
    id: number;
    name: string;
    label: string;
};

export enum NotificationType {
    INFO = 'INFO',
    ERROR = 'ERROR',
    WARNING = 'WARNING',
    SUCCESS = 'SUCCESS',
}

export type TherapySlot = {
    time: string;
    type: string;
    patient: string;
};

export type ProfilePictureDTO = {
    imageData: Uint8Array;
};

export type Session = {
    therapistName?: string;
    patientName?: string;
    action: string;
    serverTimeZone: string;
    createdAt: string;
    createdBy: string;
};

export type SessionHistoryDTO = {
    therapistName?: string;
    durationInMinutes?: number;
    therapyScheduledAt?: string;
    serverTimeZone?: string;
    patientName?: string;
    slotStatus?: string;
    description?: string;
    createdAt: string;
};

export enum AppLanguage {
    EN = 'EN',
    DE = 'DE',
    SR = 'SR',
    FR = 'FR',
    RU = 'RU',
}

export type CalendarAvailabilityDTO = {
    availabilityId?: number;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    timeZone: string;
    therapistId: number;
    createdBy?: string;
};

export type CalendarAvailabilityAndSessionsDTO = {
    calendarAvailability: CalendarAvailabilityDTO;
    reservedSessions: ReservedSessionDTO[];
};

export type ReservedSession = {
    sessionDate?: string;
    sessionTime?: string;
    durationInMinutes?: number;
    timeZone?: string;
    therapistId?: number;
    patientId?: number;
    sessionId?: number;
    guestFirstName?: string;
    guestLastName?: string;
    guestEmail?: string;
    captchaToken?: string;
    isFirstSession?: boolean;
    /** Postavlja Step 4 posle uspešnog kartičnog plaćanja — Step 6 prikazuje poruku bez „uplatite u 24h“. */
    paidWithCard?: boolean;
    /**
     * PK iz `payment_transaction` tabele — Step 4 ga setuje posle uspešnog plaćanja
     * tako da Step 5 može da fetch-uje transakcione detalje za "Thank You" stranicu
     * (Monri legalni zahtev: prikaz datuma, iznosa, AuthCode-a, order ID-ja).
     */
    paymentTransactionId?: number;

    // Snapshot vrednosti pri zadnjem uspešnom INSERT-u na BE — koristi se u Step 3
    // da se utvrdi da li korisnik (vraćen iz Step 4 sa BACK-om) hoće isti termin
    // kao prošli put. Ako jeste — preskačemo BE poziv. Ako nije — DELETE + novi INSERT.
    bookedSessionDate?: string;
    bookedSessionTime?: string;
    bookedDurationInMinutes?: number;
    supportPasssport?: boolean;
};

export type ReservedSessionDTO = {
    sessionDateTime?: string;
    durationInMinutes?: number;
    timeZone?: string;
    status?: string;
    therapistId?: number;
    patientId?: number;
    sessionId?: number;
    createdBy?: string;
    supportPasssport?: boolean;
};

export const EmptyReservedSession: ReservedSession = {
    sessionDate: '',
    sessionTime: '',
    durationInMinutes: 0,
    timeZone: '',
    therapistId: 0,
    patientId: 0,
};

export enum TherapyStatus {
    SELECTED = 'SELECTED',
    PAID = 'PAID',
    CANCELED = 'CANCELED',
    FINISHED = 'FINISHED',
    RESCHEDULED = 'RESCHEDULED',
    FIRST_SESSION = 'FIRST_SESSION',
    SCHEDULED = 'SCHEDULED',
}

export type SessionHistoryPdfParams = {
    therapistName?: string;
    status?: string;
    from?: string;
    to?: string;
};

export interface GuestScheduleRequest {
    firstName: string;
    lastName: string;
    email: string;
    sessionDateTime: string;
    durationInMinutes: number;
    timeZone: string;
    therapistId: number;
    captchaToken: string;
    status?: string;
    supportPasssport?: boolean;
}

export type SessionPricingPrimary = {
    currency: string;
    baseAmount: number;
    finalAmount: number;
    discountApplied: boolean;
    discountPercent: number;
};

export type SessionPricingSecondary = {
    currency: string;
    baseAmount: number;
};

export type SessionPricing = {
    primary: SessionPricingPrimary;
    secondary: SessionPricingSecondary[];
};

// ===== Monri Components flow (Step 4 BalancePath flow-a) =====
//
// FE više NE šalje karticne podatke ka BE-u. Umesto toga:
//   1. FE poziva BE `/payments/create-payment-session` da dobije `clientSecret`.
//   2. Monri Components SDK na FE-u koristi `clientSecret` za `confirmPayment(card, buyerInfo)`.
//   3. FE šalje finalni status + Monri transaction id na BE `/payments/confirm`.
// Tako kartica ne dotiče BalancePath BE — PCI scope ostaje na Monri-u.

/** Body za `POST /payments/create-payment-session`. */
export type CreatePaymentSessionRequest = {
    sessionId: number;
    /** ISO 4217 alpha-3 (npr. "EUR", "RSD"). */
    currency: string;
    /** IANA TZ name korisnika; BE ga koristi za `order_info` formatiranje datuma. */
    userTimeZone: string;
};

/** Response za `POST /payments/create-payment-session`. */
export type CreatePaymentSessionResponse = {
    success: boolean;
    clientSecret?: string;
    orderNumber?: string;
    /** Iznos u "minor" jedinicama (centi/para). Samo za FE prikaz/log; BE je vec naplatio Monri. */
    amountMinor?: number;
    currency?: string;
    /** PK iz `payment_transaction` tabele — FE ga šalje nazad u `/payments/confirm`. */
    paymentTransactionId?: number;
    errorMessage?: string;
};

/** Body za `POST /payments/confirm`. */
export type ConfirmPaymentRequest = {
    paymentTransactionId: number;
    /** Monri status iz `confirmPayment` response-a (APPROVED / DECLINED / FAILED / itd.). */
    monriStatus: string;
    /** Monri transaction id (`payment.transaction_response.id` ili sl.) — može biti undefined kad Monri ne vrati. */
    monriTransactionId?: string;
    /**
     * Monri `approval_code` (Authorization Code) iz `confirmPayment` response-a.
     * Po Monri legalnim smernicama prikazujemo ga kupcu na "Thank You" stranici.
     * Popunjen samo kad je transakcija APPROVED.
     */
    monriApprovalCode?: string;
    userTimeZone: string;
};

/**
 * Response za `POST /payments/confirm` (sesija) i `POST /payments/workshop/confirm` (radionica).
 * BE koristi isti DTO za obe svrhe; jedno od `sessionId` / `workshopId` je popunjeno
 * u zavisnosti od toga koji je flow u pitanju.
 */
export type PaymentResponse = {
    success: boolean;
    sessionId?: number;
    workshopId?: number;
    paymentTransactionId?: number;
    errorCode?: string;
    errorMessage?: string;
};

// ============================================================
// Workshop Monri plaćanje
// ============================================================
//
// Paralelno sa session plaćanjem: novi flow gde se `workshop_registration`
// red u BE bazi kreira TEK nakon uspešnog plaćanja kartice. Step 1 modala
// (forma sa imenom/prezimenom/email-om) ne pravi nijedan BE poziv —
// payer info se prosledjuje u `createWorkshopPaymentSession` i pamti u
// `workshop_payment_transaction` tabeli. Cena se uvek naplaćuje u RSD.

/** Body za `POST /payments/workshop/create-payment-session`. */
export type CreateWorkshopPaymentSessionRequest = {
    workshopId: number;
    payerFirstName: string;
    payerLastName: string;
    payerEmail: string;
    /** IANA TZ name korisnika; BE ga koristi za potvrdni email datum formatting. */
    userTimeZone: string;
};

/**
 * Body za `POST /workshop-registrations/bank-transfer` — javan endpoint kojim
 * korisnik prijavljuje ucesnistvo na radionicu sa uplatom bankovnim transferom
 * (uplatnica) umesto Monri kartice.
 *
 * Ne pravi Monri Payment Session. BE odmah upisuje red u `workshop_registration`
 * sa `isPaid=false`, salje email klijentu sa sazetkom prijave + linkom ka
 * `/payment-instructions` (kompletne bankovne instrukcije) + rok od 24h,
 * a admin dobija BCC kopiju u istoj SMTP transakciji.
 */
export type BankTransferWorkshopRegistrationRequest = {
    workshopId: number;
    payerFirstName: string;
    payerLastName: string;
    payerEmail: string;
    /** IANA TZ name korisnika; BE ga koristi za format datuma radionice u email-u. */
    userTimeZone: string;
};

/** Body za `POST /payments/workshop/confirm`. */
export type ConfirmWorkshopPaymentRequest = {
    /** PK iz `workshop_payment_transaction` tabele (vraćen iz `create-payment-session`). */
    workshopPaymentTransactionId: number;
    /** Monri status iz `confirmPayment` response-a (APPROVED / DECLINED / FAILED). */
    monriStatus: string;
    monriTransactionId?: string;
    /**
     * Monri `approval_code` (Authorization Code) iz `confirmPayment` response-a.
     * Po Monri legalnim smernicama prikazujemo ga kupcu na "Thank You" stranici.
     * Popunjen samo kad je transakcija APPROVED.
     */
    monriApprovalCode?: string;
    userTimeZone: string;
};

/**
 * Detalji jedne transakcije za prikaz na "Thank You" stranici po Monri legalnim
 * smernicama (uspesna i neuspesna transakcija). BE endpoint NE vraca PII (e-mail,
 * ime placatelja), samo transakcione podatke + opis usluge.
 *
 * Vraca se iz:
 *  - `GET /payments/{paymentTransactionId}/details?userTimeZone=...` (sesije)
 *  - `GET /payments/workshop/{paymentTransactionId}/details?userTimeZone=...` (radionice)
 *
 * **Vremenske zone:** sva date-time polja su vec konvertovana na BE-u
 * (`TimeConverter.convertDateTimeToUserZone`) u {@link timeZone}. Frontend ih
 * samo formatira po locale-u, BEZ dodatne TZ konverzije.
 */
export type PaymentTransactionDetails = {
    transactionType: 'SESSION' | 'WORKSHOP';
    /** PK iz odgovarajuce *_payment_transaction tabele — Monri "ID podružbine". */
    paymentTransactionId: number;
    status: 'APPROVED' | 'DECLINED' | 'FAILED' | 'PENDING';
    /**
     * Vremenska zona u kojoj su sva date-time polja izrazena
     * (IANA name, npr. "Europe/Belgrade"). Prikazuje se uz svaki datum/vreme
     * kao deo Monri legalnog zahteva.
     */
    timeZone?: string;
    /** Datum/vreme transakcije u {@link timeZone} (ISO LocalDateTime, bez offset-a). */
    transactionDateTime?: string;
    /** Iznos koji je naplacen. */
    amount?: number;
    currency?: string;
    /** Monri-jev `approval_code` (Authorization Code) — null za FAILED/DECLINED. */
    authCode?: string;
    /** Monri-jev `order_number` (npr. "BP-123-1700000000000" ili "WS-45-1700000000000"). */
    orderNumber?: string;
    therapistFirstName?: string;
    therapistLastName?: string;
    /** Datum/vreme zakazane sesije u {@link timeZone}. */
    sessionDateTime?: string;
    durationMinutes?: number;
    workshopName?: string;
    /** Datum/vreme odrzavanja radionice u {@link timeZone}. */
    workshopDateTime?: string;
};

// ============================================================

export type Workshop = {
    workshopId?: number;
    workshopName: string;
    workshopDescription: string;
    workshopDatetime: string;
    paymentStartDate: string;
    paymentEndDate: string;
    price?: number;
    isEnabled: boolean;
    workshopLink: string;
    timeZone?: string;
};

export const EmptyWorkshop: Workshop = {
    workshopName: '',
    workshopDescription: '',
    workshopDatetime: '',
    paymentStartDate: '',
    paymentEndDate: '',
    price: undefined,
    isEnabled: false,
    workshopLink: '',
};
